import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Box, ButtonBase, Chip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useContext, useMemo } from 'react';

import { useAnalytics } from '~/analytics';
import { UserContext } from '~/auth/userContext';
import { ASSISTANT_NAME } from '~/components/otto/branding';
import { buildExampleQuestions, greeting } from '~/components/otto/hero';
import { OttoAvatar } from '~/components/otto/OttoAvatar';
import { useOtto } from '~/components/otto/OttoProvider';
import { SURFACE_RADIUS } from '~/theme';

const CATEGORY_CHIPS = [
  'Tickets',
  'Callbacks',
  'Invoices',
  'Contracts',
  'Proposals',
  'Properties',
  'Compliance',
];

export function OttoHero() {
  const theme = useTheme();
  const { state, setDraft, newChat } = useOtto();
  const { track } = useAnalytics();
  const { userDetails } = useContext(UserContext);
  const firstName = userDetails.fullName?.split(' ')[0] || 'there';
  const examples = useMemo(
    () => buildExampleQuestions(state.examplePropertyName),
    [state.examplePropertyName],
  );

  const onPick = (title: string, text: string) => {
    track('chat_example_question_clicked', { question_title: title });
    // The hero renders both when no session exists yet AND when the
    // active session is empty. Auto-creating a session in the first
    // case keeps starter chips functional after the user deletes the
    // last chat — otherwise the click is a silent no-op.
    const id = state.activeSessionId ?? newChat();
    setDraft(id, text);
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 4,
      }}
    >
      <Box sx={{ textAlign: 'center', mb: 4, maxWidth: 640 }}>
        <OttoAvatar size={64} sx={{ mx: 'auto', mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 500, mb: 1 }}>
          {greeting()}, {firstName}.{' '}
          <Box
            component="span"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            What can I dig up?
          </Box>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {`I'm ${ASSISTANT_NAME}.`} Ask me anything across your tickets, callbacks, invoices,
          contracts, properties and compliance — I&apos;ll pull it together.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
          {CATEGORY_CHIPS.map((c) => (
            <Chip
              key={c}
              label={c}
              size="small"
              sx={{
                background: alpha('#fff', 0.6),
                border: `1px solid ${alpha('#000', 0.08)}`,
                fontWeight: 500,
                fontSize: '0.72rem',
              }}
            />
          ))}
        </Box>
      </Box>
      <Box sx={{ width: '100%', maxWidth: 720, mb: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            fontWeight: 600,
            mb: 1,
            display: 'block',
          }}
        >
          Try asking
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: state.mode === 'full' ? { xs: '1fr', sm: '1fr 1fr' } : '1fr',
            gap: 1.5,
          }}
        >
          {examples.map((q) => (
            <ButtonBase
              key={q.title}
              onClick={() => onPick(q.title, q.description)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                textAlign: 'left',
                p: 1.75,
                borderRadius: `${SURFACE_RADIUS}px`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                background: alpha('#fff', 0.7),
                transition: 'all 0.2s',
                '&:hover': {
                  background: '#fff',
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 18, color: theme.palette.primary.main, mt: '2px' }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 0.25 }}
                >
                  {q.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  {q.description}
                </Typography>
              </Box>
            </ButtonBase>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
